import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Banknote, 
  QrCode, 
  Clock, 
  MapPin,
  AlertTriangle,
  ShoppingCart
} from 'lucide-react';
import { PageContainer } from '../../components/ui/PageContainer';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { api } from '../../lib/api';

interface WithdrawalLimits {
  dailyLimit: number;
  dailyUsed: number;
  dailyRemaining: number;
  perTransactionLimit: number;
}

interface WithdrawalHistory {
  id: string;
  type: 'PIX_SAQUE' | 'PIX_TROCO';
  amount: number;
  cashbackAmount?: number;
  purchaseAmount?: number;
  merchantName?: string;
  merchantLocation?: string;
  status: 'PENDING' | 'COMPLETED' | 'FAILED' | 'EXPIRED';
  createdAt: string;
}

interface QrCodeData {
  qrCode: string;
  qrCodeImage: string;
  expiresAt: string;
  amount: number;
}

export default function PixSaque() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'saque' | 'troco' | 'history'>('saque');
  const [amount, setAmount] = useState('');
  const [purchaseAmount, setPurchaseAmount] = useState('');
  const [cashbackAmount, setCashbackAmount] = useState('');
  const [qrCodeData, setQrCodeData] = useState<QrCodeData | null>(null);

  // Fetch limits
  const { data: limits, isLoading: loadingLimits } = useQuery({
    queryKey: ['pix-saque-limits'],
    queryFn: async () => {
      const response = await api.get('/banking/pix-saque/limits');
      return response.data as WithdrawalLimits;
    },
  });

  // Fetch history
  const { data: history, isLoading: loadingHistory } = useQuery({
    queryKey: ['pix-saque-history'],
    queryFn: async () => {
      const response = await api.get('/banking/pix-saque/history');
      return response.data as WithdrawalHistory[];
    },
    enabled: activeTab === 'history',
  });

  // Generate QR Code mutation
  const generateQrMutation = useMutation({
    mutationFn: async (data: { amount: number; type: 'PIX_SAQUE' | 'PIX_TROCO'; purchaseAmount?: number }) => {
      const response = await api.post('/banking/pix-saque/generate', data);
      return response.data as QrCodeData;
    },
    onSuccess: (data) => {
      setQrCodeData(data);
    },
  });

  const handleGenerateSaque = () => {
    const amountCents = Math.round(parseFloat(amount) * 100);
    if (amountCents > 0) {
      generateQrMutation.mutate({ amount: amountCents, type: 'PIX_SAQUE' });
    }
  };

  const handleGenerateTroco = () => {
    const purchaseCents = Math.round(parseFloat(purchaseAmount) * 100);
    const cashbackCents = Math.round(parseFloat(cashbackAmount) * 100);
    if (purchaseCents > 0 && cashbackCents > 0) {
      generateQrMutation.mutate({ 
        amount: cashbackCents, 
        type: 'PIX_TROCO',
        purchaseAmount: purchaseCents 
      });
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value / 100);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'success' | 'warning' | 'error' | 'info'> = {
      COMPLETED: 'success',
      PENDING: 'warning',
      FAILED: 'error',
      EXPIRED: 'error',
    };
    const labels: Record<string, string> = {
      COMPLETED: 'Concluído',
      PENDING: 'Pendente',
      FAILED: 'Falhou',
      EXPIRED: 'Expirado',
    };
    return <Badge variant={variants[status] || 'info'}>{labels[status] || status}</Badge>;
  };

  const quickAmounts = [2000, 5000, 10000, 20000]; // em centavos

  return (
    <PageContainer
      title="Pix Saque e Troco"
      description="Saque dinheiro em estabelecimentos comerciais"
      icon={Banknote}
    >
      {/* Limits Card */}
      {limits && (
        <Card className="mb-6">
          <div className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-surface-400">Limite diário disponível</p>
                <p className="text-2xl font-bold text-primary-400">
                  {formatCurrency(limits.dailyRemaining)}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-surface-400">Usado hoje</p>
                <p className="text-surface-100">
                  {formatCurrency(limits.dailyUsed)} / {formatCurrency(limits.dailyLimit)}
                </p>
              </div>
            </div>
            <div className="mt-2 h-2 bg-surface-700 rounded-full overflow-hidden">
              <div 
                className="h-full bg-primary-500 rounded-full transition-all"
                style={{ width: `${(limits.dailyUsed / limits.dailyLimit) * 100}%` }}
              />
            </div>
          </div>
        </Card>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <Button
          variant={activeTab === 'saque' ? 'primary' : 'ghost'}
          onClick={() => { setActiveTab('saque'); setQrCodeData(null); }}
        >
          <Banknote className="w-4 h-4 mr-2" />
          Pix Saque
        </Button>
        <Button
          variant={activeTab === 'troco' ? 'primary' : 'ghost'}
          onClick={() => { setActiveTab('troco'); setQrCodeData(null); }}
        >
          <ShoppingCart className="w-4 h-4 mr-2" />
          Pix Troco
        </Button>
        <Button
          variant={activeTab === 'history' ? 'primary' : 'ghost'}
          onClick={() => { setActiveTab('history'); setQrCodeData(null); }}
        >
          <Clock className="w-4 h-4 mr-2" />
          Histórico
        </Button>
      </div>

      {/* Pix Saque Tab */}
      {activeTab === 'saque' && (
        <div className="space-y-6">
          {!qrCodeData ? (
            <Card>
              <div className="p-6">
                <h3 className="text-lg font-semibold text-surface-100 mb-4">
                  Quanto você quer sacar?
                </h3>
                
                {/* Quick amounts */}
                <div className="grid grid-cols-4 gap-3 mb-4">
                  {quickAmounts.map((quickAmount) => (
                    <button
                      key={quickAmount}
                      onClick={() => setAmount((quickAmount / 100).toString())}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        parseFloat(amount) * 100 === quickAmount
                          ? 'border-primary-500 bg-primary-500/10'
                          : 'border-surface-700 hover:border-surface-600'
                      }`}
                    >
                      <p className="text-surface-100 font-medium">
                        {formatCurrency(quickAmount)}
                      </p>
                    </button>
                  ))}
                </div>

                <div className="flex gap-4">
                  <Input
                    type="number"
                    placeholder="Outro valor"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="flex-1"
                    min="0"
                    step="0.01"
                  />
                  <Button
                    onClick={handleGenerateSaque}
                    disabled={!amount || parseFloat(amount) <= 0 || generateQrMutation.isPending}
                  >
                    {generateQrMutation.isPending ? 'Gerando...' : 'Gerar QR Code'}
                  </Button>
                </div>

                {limits && parseFloat(amount) * 100 > limits.perTransactionLimit && (
                  <div className="mt-4 p-3 bg-warning/10 border border-warning rounded-lg flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-warning" />
                    <p className="text-sm text-warning">
                      Valor máximo por transação: {formatCurrency(limits.perTransactionLimit)}
                    </p>
                  </div>
                )}

                <div className="mt-6 p-4 bg-surface-800 rounded-lg">
                  <h4 className="text-sm font-medium text-surface-100 mb-2">Como funciona:</h4>
                  <ol className="text-sm text-surface-400 space-y-1 list-decimal list-inside">
                    <li>Informe o valor que deseja sacar</li>
                    <li>Gere o QR Code</li>
                    <li>Apresente o QR Code no estabelecimento</li>
                    <li>O comerciante escaneia e você recebe o dinheiro</li>
                  </ol>
                </div>
              </div>
            </Card>
          ) : (
            <Card>
              <div className="p-6 text-center">
                <h3 className="text-lg font-semibold text-surface-100 mb-4">
                  QR Code para Saque
                </h3>
                
                <div className="bg-white p-4 rounded-lg inline-block mb-4">
                  <img 
                    src={qrCodeData.qrCodeImage} 
                    alt="QR Code" 
                    className="w-48 h-48"
                  />
                </div>

                <p className="text-2xl font-bold text-primary-400 mb-2">
                  {formatCurrency(qrCodeData.amount)}
                </p>
                
                <p className="text-sm text-surface-400 mb-4">
                  Válido até: {formatDate(qrCodeData.expiresAt)}
                </p>

                <div className="flex gap-4 justify-center">
                  <Button variant="secondary" onClick={() => setQrCodeData(null)}>
                    Cancelar
                  </Button>
                  <Button onClick={() => navigator.clipboard.writeText(qrCodeData.qrCode)}>
                    Copiar Código
                  </Button>
                </div>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Pix Troco Tab */}
      {activeTab === 'troco' && (
        <div className="space-y-6">
          {!qrCodeData ? (
            <Card>
              <div className="p-6">
                <h3 className="text-lg font-semibold text-surface-100 mb-4">
                  Pix Troco - Receba troco em dinheiro
                </h3>
                
                <div className="space-y-4">
                  <Input
                    type="number"
                    label="Valor da compra"
                    placeholder="0,00"
                    value={purchaseAmount}
                    onChange={(e) => setPurchaseAmount(e.target.value)}
                    min="0"
                    step="0.01"
                  />
                  
                  <Input
                    type="number"
                    label="Valor do troco (dinheiro)"
                    placeholder="0,00"
                    value={cashbackAmount}
                    onChange={(e) => setCashbackAmount(e.target.value)}
                    min="0"
                    step="0.01"
                  />

                  {purchaseAmount && cashbackAmount && (
                    <div className="p-4 bg-surface-800 rounded-lg">
                      <div className="flex justify-between mb-2">
                        <span className="text-surface-400">Valor da compra</span>
                        <span className="text-surface-100">
                          {formatCurrency(parseFloat(purchaseAmount) * 100)}
                        </span>
                      </div>
                      <div className="flex justify-between mb-2">
                        <span className="text-surface-400">Troco em dinheiro</span>
                        <span className="text-surface-100">
                          {formatCurrency(parseFloat(cashbackAmount) * 100)}
                        </span>
                      </div>
                      <div className="flex justify-between pt-2 border-t border-surface-700">
                        <span className="text-surface-100 font-medium">Total a pagar</span>
                        <span className="text-primary-400 font-bold">
                          {formatCurrency((parseFloat(purchaseAmount) + parseFloat(cashbackAmount)) * 100)}
                        </span>
                      </div>
                    </div>
                  )}

                  <Button
                    onClick={handleGenerateTroco}
                    disabled={!purchaseAmount || !cashbackAmount || generateQrMutation.isPending}
                    className="w-full"
                  >
                    {generateQrMutation.isPending ? 'Gerando...' : 'Gerar QR Code'}
                  </Button>
                </div>

                <div className="mt-6 p-4 bg-surface-800 rounded-lg">
                  <h4 className="text-sm font-medium text-surface-100 mb-2">Como funciona:</h4>
                  <ol className="text-sm text-surface-400 space-y-1 list-decimal list-inside">
                    <li>Informe o valor da compra e quanto quer de troco</li>
                    <li>Gere o QR Code</li>
                    <li>Apresente no estabelecimento</li>
                    <li>Pague a compra + troco via Pix e receba o troco em dinheiro</li>
                  </ol>
                </div>
              </div>
            </Card>
          ) : (
            <Card>
              <div className="p-6 text-center">
                <h3 className="text-lg font-semibold text-surface-100 mb-4">
                  QR Code para Pix Troco
                </h3>
                
                <div className="bg-white p-4 rounded-lg inline-block mb-4">
                  <img 
                    src={qrCodeData.qrCodeImage} 
                    alt="QR Code" 
                    className="w-48 h-48"
                  />
                </div>

                <p className="text-2xl font-bold text-primary-400 mb-2">
                  Troco: {formatCurrency(qrCodeData.amount)}
                </p>
                
                <p className="text-sm text-surface-400 mb-4">
                  Válido até: {formatDate(qrCodeData.expiresAt)}
                </p>

                <div className="flex gap-4 justify-center">
                  <Button variant="secondary" onClick={() => setQrCodeData(null)}>
                    Cancelar
                  </Button>
                  <Button onClick={() => navigator.clipboard.writeText(qrCodeData.qrCode)}>
                    Copiar Código
                  </Button>
                </div>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <Card>
          <div className="p-6">
            <h3 className="text-lg font-semibold text-surface-100 mb-4">
              Histórico de Saques
            </h3>
            {loadingHistory ? (
              <div className="text-center py-8 text-surface-400">Carregando...</div>
            ) : history && history.length > 0 ? (
              <div className="space-y-4">
                {history.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between p-4 bg-surface-800 rounded-lg"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        item.type === 'PIX_SAQUE' ? 'bg-primary-500/20' : 'bg-accent-500/20'
                      }`}>
                        {item.type === 'PIX_SAQUE' ? (
                          <Banknote className="w-5 h-5 text-primary-400" />
                        ) : (
                          <ShoppingCart className="w-5 h-5 text-accent-400" />
                        )}
                      </div>
                      <div>
                        <p className="text-surface-100 font-medium">
                          {item.type === 'PIX_SAQUE' ? 'Pix Saque' : 'Pix Troco'}
                        </p>
                        {item.merchantName && (
                          <p className="text-sm text-surface-400 flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {item.merchantName}
                          </p>
                        )}
                        <p className="text-xs text-surface-500">{formatDate(item.createdAt)}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-surface-100 font-medium">
                        {formatCurrency(item.amount)}
                      </p>
                      {item.purchaseAmount && (
                        <p className="text-xs text-surface-400">
                          Compra: {formatCurrency(item.purchaseAmount)}
                        </p>
                      )}
                      {getStatusBadge(item.status)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-surface-400">
                Nenhum saque encontrado
              </div>
            )}
          </div>
        </Card>
      )}
    </PageContainer>
  );
}
